import { mutationOptions, useMutation } from '@tanstack/react-query'
import React from 'react'
import { useDispatch } from 'react-redux'
import { getInvitedPeople } from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { InviteToOrganizationToastMessage } from 'src/app/Toast/Toast'
import { OrganizationRoles } from 'src/enums'
import { closeModal } from 'src/navigation/Lnk'
import {
  type QueryError,
  recoverBackendErrorPayload,
} from 'src/queries/network.utils'
import { postInviteToOrganization } from 'src/queries/organizations/organizationInvites.network'
import {
  INVITE_ORGANIZATION_FAILURE,
  INVITE_ORGANIZATION_INIT,
  INVITE_ORGANIZATION_SUCCESS,
  type InviteOrganizationSuccessPayload,
} from 'src/redux/actions/data/organizations.actions'
import type { PostPeopleInvitesRequest } from 'src/requests/postPeopleInvites.request'

export type InviteToOrganizationParams = {
  organizationId: string
  body: Partial<PostPeopleInvitesRequest>
  // Suppresses the refetch/closeModal/toast side effects below (e.g. the
  // silent org-invite fired from org creation) while still redux-syncing
  // INIT/SUCCESS/FAILURE for the postingOrganizationInvites/error selectors.
  silent?: boolean
  keepFlyoutOpen?: boolean
  repeat?: boolean
}

export const organizationInviteMutations = {
  invite: () =>
    mutationOptions<
      InviteOrganizationSuccessPayload,
      QueryError,
      InviteToOrganizationParams
    >({
      mutationFn: ({ organizationId, body }) =>
        postInviteToOrganization(organizationId, body),
    }),
}

/** Dispatches INIT/SUCCESS/FAILURE for OrganizationInvitesReducer/InvitedToOrgReducer. No RQ-backed read exists yet, so the 'narrow refresh' is re-dispatching getInvitedPeople for this org. */
export const useInviteToOrganization = () => {
  const dispatch = useDispatch()

  return useMutation<
    InviteOrganizationSuccessPayload,
    QueryError,
    InviteToOrganizationParams
  >({
    ...organizationInviteMutations.invite(),
    onMutate: () => {
      dispatch({ type: INVITE_ORGANIZATION_INIT })
    },
    onSuccess: (data, variables) => {
      dispatch({ type: INVITE_ORGANIZATION_SUCCESS, payload: data })
      if (!variables.silent) {
        dispatch(
          getInvitedPeople(variables.organizationId, {
            page: 0,
            perPage: 10,
          }),
        )
        if (!variables.keepFlyoutOpen) {
          dispatch(closeModal())
        }
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <InviteToOrganizationToastMessage
                people={data.organizationInvites}
                repeat={variables.repeat}
                guest={variables.body.role === OrganizationRoles.guest}
              />
            ),
          }),
        )
      }
    },
    onError: (error) => {
      dispatch({
        type: INVITE_ORGANIZATION_FAILURE,
        payload: recoverBackendErrorPayload(error),
      })
    },
  })
}
